/**
 * Batch Genealogy — Selection filters page (Project Analytics entry point).
 * Three mandatory filters (Project ID, Campaign ID, Stage-Batch Number) +
 * View Report, plus the report-capability description and info cards.
 */
import React, { useEffect, useState } from 'react';
import {
  Typography, Button, Select, MenuItem, FormControl, makeStyles,
} from '@material-ui/core';
import { fetchGenealogyFilters, fetchCampaigns, fetchBatchNumbers } from '../api';

const useStyles = makeStyles((theme) => ({
  breadcrumb: { fontSize: 14, color: '#000000', marginBottom: 6 },
  breadcrumbCurrent: { color: '#000000', fontWeight: 600 },
  title: { fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 8 },
  desc: { fontSize: 14.5, color: '#333333', lineHeight: 1.6, maxWidth: 760, marginBottom: 24 },
  card: { border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 22, marginBottom: 20 },
  filtersRow: { display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' },
  field: { display: 'flex', flexDirection: 'column', minWidth: 210, flex: 1 },
  label: { fontSize: 12.5, fontWeight: 700, color: '#000000', marginBottom: 6 },
  req: { color: '#dc2626', marginLeft: 2 },
  select: {
    background: '#fff',
    '& .MuiSelect-select': { padding: '11px 14px', fontSize: 14 },
  },
  viewBtn: {
    background: '#ea580c', color: '#fff', textTransform: 'none', fontWeight: 700, fontSize: 15,
    borderRadius: 8, padding: '11px 26px', height: 44, boxShadow: 'none',
    '&:hover': { background: '#c2410c' },
    '&.Mui-disabled': { background: '#fdba74', color: '#fff' },
  },
  tip: { fontSize: 13.5, color: '#333333', marginTop: 18, borderTop: '1px solid #f1f5f9', paddingTop: 14 },
  tipStrong: { color: '#000000', fontWeight: 700 },
}));

const SelectionFilters = ({ filters, setFilters, onViewReport, loading }) => {
  const classes = useStyles();
  const [projects, setProjects] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [batches, setBatches] = useState([]);

  // The chosen values are owned by the parent (index.jsx) so they survive a
  // round-trip to the report and back.
  const { projectId, campaignId, batchNumber } = filters;

  // Cascade resets live in the change handlers, NOT in effects — so returning to
  // this page re-hydrates the previous selection from the parent without an
  // on-mount effect wiping it. Changing a filter still clears everything below it.
  const handleProject = (v) => setFilters({ projectId: v, campaignId: '', batchNumber: '' });
  const handleCampaign = (v) => setFilters((f) => ({ ...f, campaignId: v, batchNumber: '' }));
  const handleBatch = (v) => setFilters((f) => ({ ...f, batchNumber: v }));

  // Load the project list once.
  useEffect(() => {
    fetchGenealogyFilters().then((res) => {
      setProjects(res.data.projects || []);
    });
  }, []);

  // Campaigns for the selected project (runs on mount too, to restore the list
  // for a re-hydrated project).
  useEffect(() => {
    if (!projectId) { setCampaigns([]); return; }
    fetchCampaigns({ projectId }).then((res) => {
      setCampaigns(res.data || []);
    });
  }, [projectId]);

  // Batches for the selected project + campaign.
  useEffect(() => {
    if (!projectId || !campaignId) { setBatches([]); return; }
    fetchBatchNumbers({ projectId, campaignId }).then((res) => {
      setBatches(res.data || []);
    });
  }, [projectId, campaignId]);

  const ready = projectId && campaignId && batchNumber && !loading;

  return (
    <div>
      <div className={classes.breadcrumb}>
        ⌂ Dashboard&nbsp; / &nbsp;<span className={classes.breadcrumbCurrent}>Batch Genealogy</span>
      </div>
      <Typography className={classes.title}>Batch Genealogy Report</Typography>
      <Typography className={classes.desc}>
        Trace the full ancestry of any batch — every parent lot that fed into it, down to raw
        materials — with deviation, yield and release status surfaced inline. Enter the Project,
        Campaign and batch number to begin.
      </Typography>

      <div className={classes.card}>
        <div className={classes.filtersRow}>
          <div className={classes.field}>
            <span className={classes.label}>Project ID<span className={classes.req}>*</span></span>
            <FormControl variant="outlined" size="small">
              <Select
                className={classes.select}
                value={projectId}
                displayEmpty
                onChange={(e) => handleProject(e.target.value)}
              >
                <MenuItem value="" disabled>Select Project ID…</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={classes.field}>
            <span className={classes.label}>Campaign ID<span className={classes.req}>*</span></span>
            <FormControl variant="outlined" size="small">
              <Select
                className={classes.select}
                value={campaignId}
                displayEmpty
                disabled={!projectId}
                onChange={(e) => handleCampaign(e.target.value)}
              >
                <MenuItem value="" disabled>Select Campaign ID…</MenuItem>
                {campaigns.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={classes.field}>
            <span className={classes.label}>Stage - Batch Number<span className={classes.req}>*</span></span>
            <FormControl variant="outlined" size="small">
              <Select
                className={classes.select}
                value={batchNumber}
                displayEmpty
                disabled={!campaignId}
                onChange={(e) => handleBatch(e.target.value)}
              >
                <MenuItem value="" disabled>Select Batch…</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <Button
            className={classes.viewBtn}
            variant="contained"
            disabled={!ready}
            onClick={() => onViewReport({ projectId, campaignId, batchNumber })}
          >
            {loading ? 'Loading…' : 'View Report'}
          </Button>
        </div>

        <div className={classes.tip}>
          <span className={classes.tipStrong}>Tip:</span> pick a final-product (Step E) batch to see the full A→E lineage.
        </div>
      </div>
    </div>
  );
};

export default SelectionFilters;
